// Helper functions for competition management

// Format seconds -> mm:ss
export const formatTime = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Format currency helper
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Helper to normalize roles
export const normalizeRoles = (roleValue) => {
  if (!roleValue) return [];
  if (Array.isArray(roleValue) && typeof roleValue[0] === "string") {
    return roleValue;
  }
  if (
    Array.isArray(roleValue) &&
    typeof roleValue[0] === "object" &&
    "S" in roleValue[0]
  ) {
    return roleValue.map((r) => r.S);
  }
  if (typeof roleValue === "string") {
    return [roleValue];
  }
  return [];
};

// Helper to normalize dancer list
export const normalizeDancerList = (dancers) => {
  if (!dancers) return [];
  if (
    Array.isArray(dancers) &&
    typeof dancers[0] === "object" &&
    "S" in dancers[0]
  ) {
    return dancers.map((d) => d.S);
  }
  return Array.isArray(dancers) ? dancers : [];
};

// Get routine type label
export const getRoutineTypeLabel = (typeId) => {
  return typeId ? typeId.charAt(0).toUpperCase() + typeId.slice(1) : 'Unknown';
};

// Determine routine type based on dancer count and pricing rules
export const getRoutineType = (dancerCount, competitionPricingRules) => {
  if (!Array.isArray(competitionPricingRules) || competitionPricingRules.length === 0) {
    console.warn("Competition pricing rules not available for determining routine type.");
    return 'Unknown';
  }
  
  // Find the rule where the dancer count fits the min/max range
  const rule = competitionPricingRules.find(
    r => dancerCount >= (r.minDancers ?? 1) && dancerCount <= (r.maxDancers ?? 999)
  );
  
  // Return the category name from the rule, or a default
  return rule?.category || 'Group';
};

// Get price for a routine based on its dancer count
export const getRoutinePrice = (routine, competitionPricingRules) => {
  if (!routine || !Array.isArray(competitionPricingRules) || competitionPricingRules.length === 0) {
    return 0;
  }

  const dancerCount = Array.isArray(routine.dancers) ? routine.dancers.length : 0;
  const routineType = getRoutineType(dancerCount, competitionPricingRules);

  // Find the specific pricing rule matching the determined routine type
  const rule = competitionPricingRules.find(r => r.category === routineType);

  if (!rule) {
    return 0;
  }

  // Calculate price based on the rule type
  const pricePerUnit = parseFloat(rule.discountRate || 0);

  if (rule.pricingType === 'perDancer') {
    return pricePerUnit * dancerCount;
  } else if (rule.pricingType === 'flatRate') {
    return pricePerUnit;
  } else {
    return pricePerUnit;
  }
};

// Get age division based on average age
export const getAgeDivision = (age, ageDivisions) => {
  if (age === null || age === undefined) return 'Unknown';
  
  // Filter to only include competitive age groups
  const competitiveAgeDivisions = ageDivisions.filter(div => 
    // Only include groups explicitly marked as competitive (isCompetitive === true)
    div.isCompetitive === true
  );
  
  // Log for debugging
  console.log(`Filtering age divisions for competition. Original: ${ageDivisions.length}, Competitive only: ${competitiveAgeDivisions.length}`);
  console.log('Competitive age divisions:', competitiveAgeDivisions);
  
  // Find the competitive division that matches the age
  const division = competitiveAgeDivisions.find(div => {
    // Handle both min/max and minAge/maxAge property names for compatibility
    const minAge = div.minAge !== undefined ? div.minAge : (div.min !== undefined ? div.min : 0);
    const maxAge = div.maxAge !== undefined ? div.maxAge : (div.max !== undefined ? div.max : 999);
    
    // Handle both nulls and hasNoMin/MaxAge properties
    const effectiveMinAge = (div.hasNoMinAge || minAge === null) ? 0 : minAge;
    const effectiveMaxAge = (div.hasNoMaxAge || maxAge === null) ? 999 : maxAge;
    
    return age >= effectiveMinAge && age <= effectiveMaxAge;
  });
  
  // Log the found division for debugging
  console.log(`Determined age group for age ${age}:`, division?.label || 'Unknown');
  
  return division ? division.label : 'Unknown';
};

// Extract dancers from routines
export const extractDancersFromRoutines = (selectedRoutines, roster) => {
  // Create a map to track dancers and their routines
  const dancerMap = new Map();
  const dancerIdSet = new Set();
  
  selectedRoutines.forEach(routine => {
    const routineDancers = normalizeDancerList(routine.dancers);
    const routineName = routine.title || routine.name || 'Untitled Routine';
    
    if (routineDancers && routineDancers.length > 0) {
      routineDancers.forEach(dancerId => {
        dancerIdSet.add(dancerId);
        
        const dancerFromRoster = roster.find(d => d.dancerId === dancerId);
        
        if (dancerFromRoster) {
          if (dancerMap.has(dancerId)) {
            const dancer = dancerMap.get(dancerId);
            if (!dancer.routines.includes(routineName)) {
              dancer.routines.push(routineName);
            }
          } else {
            dancerMap.set(dancerId, {
              ...dancerFromRoster,
              dancerId,
              fromRoutine: routineName,
              routines: [routineName],
              isFromRoutine: true
            });
          }
        }
      });
    }
  });
  
  // Convert map to array for UI display
  const allDancers = Array.from(dancerMap.values());
  // Convert set to array for backend IDs
  const dancerIds = Array.from(dancerIdSet);
  
  return {
    dancersFromRoutines: allDancers,
    dancerIds: dancerIds
  };
};

// Calculate total price for all selected routines
export const calculateTotalPrice = (selectedRoutines, competitionPricingRules) => {
  return selectedRoutines.reduce((total, routine) => {
    return total + getRoutinePrice(routine, competitionPricingRules);
  }, 0);
}; 